import type { ScheduleSummary } from '@dyspatch/core';
import { useNavigate } from '@tanstack/react-router';
import { Pause, Play, Trash2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AskBox, useAsk } from '@/features/actions/AskBox';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * The verbs on one schedule (docs/JOB-LIFECYCLE.md 2.7): Run now, pause, resume,
 * delete.
 *
 * Pause and delete ask in place with a confirm button, like every other
 * destructive verb in the console; Run now and Resume do not, because neither
 * loses anything a person cannot get back.
 *
 * All four are the approver's or an admin's. The hub is what enforces that and
 * answers a stranger with a sentence; `ScheduleDetail` decides whether to draw
 * this row at all, the same way the job detail decides whether to draw Amend.
 */

/** The chip a paused schedule wears everywhere, carrying its own reason. */
export function SchedulePausedChip({
  reason,
  className,
}: {
  reason?: string | null;
  className?: string;
}) {
  return (
    <Badge
      variant="warning"
      className={className}
      title={reason ?? 'Paused. Nothing fires until it is resumed.'}
    >
      <Pause className="size-2.5" aria-hidden />
      Paused
    </Badge>
  );
}

const TOUCH = 'max-sm:h-9 max-sm:px-3';

export function ScheduleActions({
  schedule,
  className,
}: {
  schedule: ScheduleSummary;
  className?: string;
}) {
  const ask = useAsk();
  const navigate = useNavigate();

  /**
   * The hub answers with what the firing actually did, so the line is written
   * from the report rather than assumed. A skip is a normal outcome, not a
   * failure: saying "filed" when it skipped would be the console inventing a job
   * that does not exist, which is exactly the trust this page is for.
   */
  const runNow = useHubAction(async () => {
    const { report } = await api.runSchedule(schedule.id);
    if (report.outcome === 'filed') {
      toast.success('Filed. The job is approved and its tasks are queued.');
    } else if (report.outcome === 'skipped') {
      toast.message(report.reason ? `Nothing filed: ${report.reason}.` : 'Nothing filed.');
    } else {
      toast.error(report.reason ? `That run failed: ${report.reason}` : 'That run failed.');
    }
    return report;
  });
  const pause = useHubAction(
    (reason: string) => api.pauseSchedule(schedule.id, reason || undefined),
    { onDone: () => ask.close(), success: 'Paused. Nothing fires until you resume it.' },
  );
  const resume = useHubAction(() => api.resumeSchedule(schedule.id), {
    success: 'Resumed. The failure count is back to zero.',
  });
  const remove = useHubAction(() => api.deleteSchedule(schedule.id), {
    onDone: () => {
      ask.close();
      void navigate({ to: '/schedules' });
    },
    success: `"${schedule.name}" deleted. The jobs it filed stay.`,
  });

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-1.5 px-gutter py-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => runNow.run()}
          disabled={runNow.busy || Boolean(schedule.pausedAt)}
          className={TOUCH}
          title={
            schedule.pausedAt
              ? 'Paused. Resume it first.'
              : 'Files this job now, through the same claim the clock uses.'
          }
        >
          <Zap aria-hidden />
          {runNow.busy ? 'Running…' : 'Run now'}
        </Button>

        {schedule.pausedAt ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => resume.run()}
            disabled={resume.busy}
            className={TOUCH}
          >
            <Play aria-hidden />
            {resume.busy ? 'Resuming…' : 'Resume'}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => ask.open('pauseSchedule', schedule.id)}
            className={TOUCH}
          >
            <Pause aria-hidden />
            Pause
          </Button>
        )}

        <Button
          size="sm"
          variant="danger"
          className={cn('ml-auto', TOUCH)}
          onClick={() => ask.open('delete-schedule', schedule.id)}
        >
          <Trash2 aria-hidden />
          Delete
        </Button>
      </div>

      {ask.isOpen('pauseSchedule', schedule.id) ? (
        <AskBox
          ask={ask.ask!}
          busy={pause.busy}
          error={pause.error}
          onCancel={ask.close}
          onConfirm={(value) => pause.run(value)}
        />
      ) : null}
      {ask.isOpen('delete-schedule', schedule.id) ? (
        <AskBox
          ask={ask.ask!}
          busy={remove.busy}
          error={remove.error}
          onCancel={ask.close}
          onConfirm={() => remove.run()}
        />
      ) : null}
    </div>
  );
}
